import React from 'react';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';

import { DefaultLayoutProvider } from 'hellosign/common/layout/context';
import type { LayoutContextShape } from 'hellosign/common/layout/context';
import { HSUserProvider } from 'hellospa/common/hs-user';
import type { HSUser } from 'hellospa/common/hs-user';
import { useFeatureFlags } from 'js/sign-components/common/feature-flag';
import { setSiteCode } from 'js/sign-components/common/use-site-code';
import { AppShell } from '../app-shell';
import type { AppShellNavigationItem } from '../create-app-shell-layout';
import { LegacyContentHost } from './legacy-content-host';
import styles from './legacy-document-app-shell.module.scss';

export const LEGACY_DOCUMENT_CONTENT_ID = 'legacy-document-content';

/**
 * The user fields the AppShell header, navigation and fallback layout read.
 * inAppLayout already prepares them for the legacy top bar and left menu, so the
 * PHP document gains no account preparation.
 */
export type LegacyDocumentUser = Pick<
  HSUser,
  | 'id'
  | 'guid'
  | 'emailAddress'
  | 'name'
  | 'isFree'
  | 'referUrl'
  | 'numDocsLeft'
  | 'numApiDocsLeft'
  | 'numTemplatesLeft'
  | 'primarySignatureGuid'
  | 'showApiCredits'
  | 'canAccessAdminConsole'
  | 'isFromSelfServeApi'
  | 'isDropboxManaged'
  | 'showDelinquencyBanner'
  | 'nextBillingDate'
  | 'endDate'
  | 'canManageBilling'
  | 'hasFrozenSubscription'
  | 'hasAlternativeBilling'
  | 'prevPlanName'
  | 'freeTrialEndDate'
  | 'showExpiredCCNotification'
  | 'ccExpiredBeforeRenewal'
  | 'ccExpiringBeforeRenewal'
  | 'canAccessBulkSendUI'
  | 'canAccessTemplateConverter'
  | 'isNewTemplateConverterEnabled'
  | 'isOldTemplateConverterEnabled'
  | 'canAccessSignFaxBridge'
  | 'isSandBoxedLeoUser'
> & {
  settings: Pick<HSUser['settings'], 'firstName' | 'lastName'>;
  showApi: boolean;
};

export type LegacyDocumentAppShellProps = {
  app: LayoutContextShape['app'];
  selectedNavigationItem?: AppShellNavigationItem | null;
  user: LegacyDocumentUser;
  layout: {
    csrfToken: string;
    siteCode: 'S' | 'F';
    signatureType: string | null;
  };
};

/**
 * Renders a PHP document's content in AppShell. inAppLayout renders this instead of
 * its legacy top bar and left menu when the document opted in, and puts the
 * document's content in a hidden element for LegacyContentHost to move into the
 * shell.
 */
export default function LegacyDocumentAppShell({
  app,
  layout,
  selectedNavigationItem,
  user,
}: LegacyDocumentAppShellProps) {
  setSiteCode(layout.siteCode);

  const featureFlags = useFeatureFlags();
  const [history] = React.useState(() => createBrowserHistory());
  const layoutContext = React.useMemo(
    (): LayoutContextShape => ({
      app,
      csrfToken: layout.csrfToken,
      featureFlags,
      isDropbox: false,
      isEmbedded: false,
      isSpa: true,
      pageProps: {},
      privacyURL: '',
      signature: { type: layout.signatureType === 'I' ? 'I' : 'S' },
      siteCode: layout.siteCode,
      termsURL: '',
      // Fields outside LegacyDocumentUser are not read by the shell or its fallback.
      user: user as unknown as HSUser,
    }),
    [app, featureFlags, layout, user],
  );

  return (
    // The legacy left navigation reads the location from a Router.
    <Router history={history}>
      <HSUserProvider value={layoutContext.user}>
        <DefaultLayoutProvider value={layoutContext}>
          <AppShell
            selectedNavigationItem={selectedNavigationItem ?? undefined}
          >
            <LegacyContentHost
              className={styles.content}
              elementId={LEGACY_DOCUMENT_CONTENT_ID}
            />
          </AppShell>
        </DefaultLayoutProvider>
      </HSUserProvider>
    </Router>
  );
}
